import { clampNumber, isFiniteNumber } from './untils';

const DEFAULT_EDGE_THRESHOLD_PX = 48;
const DEFAULT_MIN_SPEED_PX_PER_FRAME = 2;
const DEFAULT_MAX_SPEED_PX_PER_FRAME = 20;

/**
 * Computes per-frame scroll delta when pointer is near container edges.
 */
export function getAutoScrollDelta({
  pointerY,
  containerTop,
  containerHeight,
  edgeThresholdPx = DEFAULT_EDGE_THRESHOLD_PX,
  minSpeedPxPerFrame = DEFAULT_MIN_SPEED_PX_PER_FRAME,
  maxSpeedPxPerFrame = DEFAULT_MAX_SPEED_PX_PER_FRAME,
}: {
  pointerY: number;
  containerTop: number;
  containerHeight: number;
  edgeThresholdPx?: number;
  minSpeedPxPerFrame?: number;
  maxSpeedPxPerFrame?: number;
}): number {
  if (
    !isFiniteNumber(pointerY) ||
    !isFiniteNumber(containerTop) ||
    !isFiniteNumber(containerHeight)
  ) {
    return 0;
  }

  if (containerHeight <= 0) return 0;
  if (edgeThresholdPx <= 0) return 0;

  const containerBottom = containerTop + containerHeight;
  const safeMinSpeed = Math.max(0, minSpeedPxPerFrame);
  const safeMaxSpeed = Math.max(safeMinSpeed, maxSpeedPxPerFrame);

  const topZoneEnd = containerTop + edgeThresholdPx;
  if (pointerY < topZoneEnd) {
    const distanceIntoZone = topZoneEnd - pointerY;
    const ratio = clampNumber({
      value: distanceIntoZone / edgeThresholdPx,
      min: 0,
      max: 1,
    });

    return -(safeMinSpeed + (safeMaxSpeed - safeMinSpeed) * ratio);
  }

  const bottomZoneStart = containerBottom - edgeThresholdPx;
  if (pointerY > bottomZoneStart) {
    const distanceIntoZone = pointerY - bottomZoneStart;
    const ratio = clampNumber({
      value: distanceIntoZone / edgeThresholdPx,
      min: 0,
      max: 1,
    });

    return safeMinSpeed + (safeMaxSpeed - safeMinSpeed) * ratio;
  }

  return 0;
}

/**
 * Applies auto-scroll delta and returns whether a scroll position change happened.
 */
export function applyAutoScroll({
  scrollTop,
  scrollHeight,
  clientHeight,
  delta,
}: {
  scrollTop: number;
  scrollHeight: number;
  clientHeight: number;
  delta: number;
}): {
  didScroll: boolean;
  nextScrollTop: number;
} {
  if (
    !isFiniteNumber(scrollTop) ||
    !isFiniteNumber(scrollHeight) ||
    !isFiniteNumber(clientHeight) ||
    !isFiniteNumber(delta)
  ) {
    return {
      didScroll: false,
      nextScrollTop: 0,
    };
  }

  const maxScrollTop = Math.max(0, scrollHeight - clientHeight);

  const nextScrollTop = clampNumber({
    value: scrollTop + delta,
    min: 0,
    max: maxScrollTop,
  });

  return {
    didScroll: nextScrollTop !== scrollTop,
    nextScrollTop,
  };
}
